<template>
  <v-container class="pa-0">
    <v-row class="pt-5 d-flex flex-row justify-center">
      <v-btn-toggle
        v-model="selectedIntervalIndex"
        mandatory
        color="primary"
      >
        <template v-for="ii, i in intervals">
          <v-btn
            :key="`pi_${i}`"
            outlined
          >
            {{ ii.intervalText }}
          </v-btn>
          <v-badge
            v-if="i > 0 && i === intervals.length-1"
            :key="`bi_${i}`"
            color="success"
            overlap
          >
            <template #badge>
              <div class="mx-1">
                <v-icon
                  x-small
                  style="padding-right: 3px; padding-bottom: 2px; font-size: 12px !important;"
                  class="mr-0"
                >
                  mdi-star
                </v-icon>
                {{ $i18n.t('label.best_value') }}
              </div>
            </template>
          </v-badge>
        </template>
      </v-btn-toggle>
    </v-row>
    <v-row class="d-flex flex-row justify-center">
      <template v-for="p,i in prices">
        <v-col
          :key="`price_${i}`"
          cols="12"
          sm="6"
          md="4"
        >
          <plan-details-card
            :features="productFeatures[i]"
            :price="prices[i]"
            :show-discount="discountInfos.length > 1"
            :current-plan="p?.product?.subscriptionTierLevel === currentSubscriptionLevel"
            :disabled="(p?.product?.subscriptionTierLevel ?? 0) <= currentSubscriptionUpgradableLevel"
            :unavailable-plan="currentSubscriptionUpgradableLevel > 0 && (p?.product?.subscriptionTierLevel ?? 0) < currentSubscriptionLevel"
            @plan-selected="selectPrice(p)"
          />
        </v-col>
      </template>

      <v-col
        cols="12"
        class="pa-0 ma-0"
      >
        <i18n
          path="label.billing_all_prices_are_in"
          tag="p"
          class="text-secondary text-center pa-0 ma-0 pt-1 pb-4"
        >
          <currency-picker
            v-if="currencySelectorEnabled"
            :value="selectedCurrency"
            @input="setCurrency"
          />
          <span v-else>
            {{ currencyText }}
          </span>
        </i18n>
        <div
          v-if="discountInfos.length === 1"
          class="text-center pb-2"
        >
          <template v-if="discountInfos[0] && discountInfos[0].offerExpiresDescription">
            {{ discountInfos[0].offerExpiresDescription }}
          </template>
          <template v-if="discountInfos[0] && discountInfos[0].validUntilOrDurationDescription">
            {{ discountInfos[0].validUntilOrDurationDescription }}
          </template>
        </div>
        <!-- <v-divider /> -->
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
/* eslint-disable vue/max-len */

import {
  ref, Ref, onMounted, computed, watch, nextTick, inject,
} from 'vue';
import {
  Currency, Customer, Invoice, Price, Product,
  Subscription,
} from '@/api/api-billing';
import {
  billingCurrencyText, SubscriptionPriceInfo, subscriptionPriceInfo,
  productPriceWithInterval,
  SubscriptionInterval,
  sortedSubscriptionIntervals,
  subscriptionUpgradableLevel,
} from '@/services/billing-utils';
import CurrencyPicker from '@/components/pickers/CurrencyPicker.vue';
import { performAction } from '@/services/vue-utils';
import i18nn from '@/plugins/i18n';
import store from '@/store/store';
import { PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';
import VueI18n from 'vue-i18n';
import PlanDetailsCard from './PlanDetailsCard.vue';

export interface PlanSelectedEvent {
  price: Price
  previewInvoice?: Invoice,
}

const featuresPro = [
  i18nn.t('plans.pro.feature_no_cover_fees'),
  i18nn.t('plans.pro.feature_deposit_cost', ['€2.99']),
  i18nn.t('plans.pro.feature_unlimited_reservations'),
  i18nn.t('plans.pro.feature_rwg'),
  i18nn.t('plans.pro.feature_dashboard'),
  i18nn.t('plans.pro.feature_giftcards'),
  i18nn.t('plans.pro.feature_app'),
  i18nn.t('plans.pro.feature_email_support'),
];

const featuresBusiness = [
  i18nn.t('plans.business.feature_all_from_pro'),
  i18nn.t('plans.pro.feature_deposit_cost', ['€0.99']),
  i18nn.t('plans.business.feature_feedback'),
  i18nn.t('plans.business.feature_waitlist'),
  i18nn.t('plans.business.feature_custom_giftcard_design'),
  i18nn.t('plans.business.feature_integrations'),
  i18nn.t('plans.business.feature_newsletter'),
];

const featuresUltimate = [
  i18nn.t('plans.ultimate.feature_all_from_business'),
  i18nn.t('plans.pro.feature_deposit_cost', ['€0']),
  i18nn.t('plans.ultimate.feature_account_manager'),
  i18nn.t('plans.ultimate.feature_phone_support'),
  i18nn.t('plans.ultimate.feature_export_api'),
];

const productFeatures = [featuresPro, featuresBusiness, featuresUltimate];

const props = defineProps({});

const emit = defineEmits<{(e: 'submit', event: PlanSelectedEvent): void}>();

const customer = computed<Customer|undefined>(() => store.getters.billingCustomer ?? undefined);
const subscription = computed<Subscription|undefined>(() => store.getters.billingSubscription);
const currencySelectorEnabled = computed(() => customer.value?.currency === undefined);
const selectedCurrency = ref(Currency.EUR) as Ref<string>;
const currencyText = computed(() => billingCurrencyText(selectedCurrency.value));

const currentSubscriptionLevel = computed<number>(() => subscription.value?.price?.product?.subscriptionTierLevel ?? 0);
const currentSubscriptionUpgradableLevel = computed<number>(() => subscriptionUpgradableLevel(subscription.value));
const products = computed<Product[]>(() => store.getters.billingSubscriptionPriceProducts as Product[]);

const intervals = computed<SubscriptionInterval[]>(() => sortedSubscriptionIntervals(products.value, selectedCurrency.value));
const selectedIntervalIndex = ref(0);
const selectedInterval = computed(() => intervals.value.at(selectedIntervalIndex.value));

const prices = computed<Price[]>(() => products.value.flatMap((p) => productPriceWithInterval(p, selectedCurrency.value, selectedInterval.value?.interval, selectedInterval.value?.intervalCount) ?? []));
const priceInfos = computed<SubscriptionPriceInfo[]>(() => prices.value?.map((p) => subscriptionPriceInfo(p)));
const selectedPrice = ref(undefined) as Ref<Price|undefined>;

const discountInfos = computed(() => {
  const dinfos = priceInfos.value.flatMap((pi) => pi.discountInfo ?? []);
  // no or single discount found
  if (dinfos.length < 2) return dinfos;
  // more but all same
  if (dinfos.every((di) => di.offerExpiresDescription === dinfos[0].offerExpiresDescription && di.validUntilOrDurationDescription === dinfos[0].validUntilOrDurationDescription)) return [dinfos[0]];
  // multiple different
  return priceInfos.value.map((pi) => pi.discountInfo);
});

onMounted(async () => {
  selectedCurrency.value = store.getters.billingGuessedCurrency;
  await nextTick();
  selectedIntervalIndex.value = intervals.value.length > 0 ? intervals.value.length - 1 : 0;
});

async function selectPrice(price: Price) {
  console.log('selectPrice: ', price);
  selectedPrice.value = price;

  // eslint-disable-next-line no-use-before-define
  if (submitStep) submitStep(paymentWizardStep.value);
  else emit('submit', { price });
}

async function submit() {
  if (!selectedPrice.value) return undefined;

  let invoice = undefined as Invoice|undefined;

  const ok = await performAction(
    null,
    i18nn.tc('error.billing_general'),
    async () => { invoice = await store.dispatch('billingPreviewSubscription', { priceID: selectedPrice.value?.ID }); },
  );
  if (!ok) return undefined;

  const e: PlanSelectedEvent = {
    price: selectedPrice.value,
    previewInvoice: invoice,
  };

  return e;
}

function setCurrency(currency: string) {
  console.log('setCurrency: ', currency);
  selectedCurrency.value = currency;
}

watch(
  () => selectedInterval.value,
  (value) => { console.log('selectedInterval changed: ', selectedInterval.value); },
);

watch(
  () => prices.value,
  (value) => { console.log('prices changed: ', prices.value); },
);

// wizard embeddable
const addStep = inject('addStep') as ((s: PaymentWizardStep) => void) | undefined;
const stepChanged = inject('stepChanged') as ((s: PaymentWizardStep) => void) | undefined;
const submitStep = inject('submitStep') as ((s: PaymentWizardStep) => void) | undefined;

const paymentWizardStep = computed(() => ({
  key: PaymentWizardStepKey.PlanSelector,
  label: i18nn.tc('title.select_plan'),
  complete: selectedPrice.value !== undefined,
  hideSubmit: true,
  submitAction: submit,
} as PaymentWizardStep));

watch(
  () => paymentWizardStep.value,
  (value) => { if (stepChanged) stepChanged(paymentWizardStep.value); console.log('paymentWizardStep: ', customer); },
);

if (addStep) addStep(paymentWizardStep.value);

</script>
